import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalConfirm, type ModalConfirmProps, type TestProps } from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import { BlockingConfirmModal } from '../../components/BlockingConfirmModal';

export type ConfirmData = {
    /** Заголовок модалки. */
    title: string | ReactNode;
    /** Текст под заголовком. */
    bodyText?: string | ReactNode;
    /** Текст кнопки подтверждения. По умолчанию — «Ок». */
    applyText?: string;
    /** Подпись под кнопкой подтверждения. */
    applyCaption?: string;
    /** Текст кнопки отмены. По умолчанию — «Отмена». Скрыть кнопку — hideCancel. */
    cancelText?: string;
    /** Скрыть кнопку отмены. Для уведомлений с одной кнопкой («Понятно», «Закрыть» и т.п.). */
    hideCancel?: boolean;
    /** Иллюстрация над текстом. */
    imageSrc?: string;
    /** Колбэк кнопки подтверждения. Не вызывается при закрытии через X/ESC/клик вне модалки. */
    onApply?: () => void;
    /** Колбэк кнопки отмены. Не вызывается при закрытии через X/ESC/клик вне модалки — там только resolve(false). */
    onCancel?: () => void;
    /** Запрещает закрытие модалки через X, ESC и клик вне. Использовать только при крайней необходимости. */
    isBlocking?: boolean;
    /** Явный data-testid кнопки подтверждения (иначе `{data-testid}.Confirm`). */
    applyButtonTestId?: string;
    /** Явный data-testid кнопки отмены (иначе `{data-testid}.Cancel`). */
    cancelButtonTestId?: string;
} & TestProps;

export function useConfirm() {
    const [data, setData] = useState<ConfirmData | undefined>();
    const { value: isModalOpen, toggleOn: openModal, toggleOff: closeModal } = useToggle(false);

    const refResolve = useRef<(confirmed: boolean) => void>();

    const settle = useCallback((confirmed: boolean) => {
        refResolve.current?.(confirmed);
        refResolve.current = undefined;
    }, []);

    useEffect(
        () => () => {
            refResolve.current?.(false);
            refResolve.current = undefined;
        },
        []
    );

    const confirm = useCallback(
        (props: ConfirmData): Promise<boolean> => {
            if (refResolve.current) {
                settle(false);
            }

            setData(props);
            openModal();

            return new Promise<boolean>((resolve) => {
                refResolve.current = resolve;
            });
        },
        [openModal, settle]
    );

    const buttons = useMemo<ModalConfirmProps['buttons']>(() => {
        const applyButton = {
            color: 'accent',
            text: data?.applyText ?? 'Ок',
            ...(data?.applyCaption ? { caption: data.applyCaption } : {}),
            // Чтобы по 'Enter'-у случался confirm
            autoFocus: true,
            onClick: () => {
                data?.onApply?.();
                settle(true);
                closeModal();
            },
            ...(data?.applyButtonTestId ? { 'data-testid': data.applyButtonTestId } : {}),
        } as const;

        const cancelButton = {
            color: 'normal',
            text: data?.cancelText ?? 'Отмена',
            onClick: () => {
                data?.onCancel?.();
                settle(false);
                closeModal();
            },
            ...(data?.cancelButtonTestId ? { 'data-testid': data.cancelButtonTestId } : {}),
        } as const;

        return data?.hideCancel ? [applyButton] : [applyButton, cancelButton];
    }, [data, closeModal, settle]);

    const handleClose = useCallback(() => {
        settle(false);
        closeModal();
    }, [closeModal, settle]);

    const renderConfirmModal = useCallback(() => {
        if (!isModalOpen || !data) {
            return null;
        }

        return data.isBlocking ? (
            <BlockingConfirmModal
                isOpen={isModalOpen}
                onClose={handleClose}
                title={data.title}
                buttons={buttons}
                bodyText={data.bodyText}
                imageSrc={data.imageSrc}
                data-testid={data['data-testid']}
            />
        ) : (
            <ModalConfirm
                headerText={data.title}
                bodyText={data.bodyText}
                buttons={buttons}
                isOpen={isModalOpen}
                onClose={handleClose}
                imageSrc={data.imageSrc}
                preventScroll={false}
                data-testid={data['data-testid']}
            />
        );
    }, [isModalOpen, data, buttons, handleClose]);

    return { renderConfirmModal, confirm, isConfirming: isModalOpen };
}
